import logUtil from './log-util'
import axios from './axios-util'
import {
  recordCplStart,
  recordCplEnd,
} from '../components/common/analytics/utils'
import { ELMO } from '../common/constant'
const logger = logUtil
const APPLICATION_JSON = 'application/json'

const apiFactory = {}

const endCPL = ({ url, event, status, log, err, payload }) => {
  if (err) {
    logger.error(`${log} failed.`, payload)
  } else {
    logger.info(`${log} completed.`, payload)
  }
  recordCplEnd(url, {
    cpl_event: event ? event : 'api load',
    status: status ? status : 500,
  })
}

const startCPL = ({ url, log, payload }) => {
  logger.info(`${log} starting.`, payload)
  recordCplStart(url)
}

apiFactory.loadAllAppData = async ({ queryString }) => {
  const TARGET_URL = '/dashboard/api/applications'
  const LOADING_APPS = 'Loading apps'
  let allAppData
  try {
    startCPL({ url: TARGET_URL, log: LOADING_APPS })

    const response = await axios.get(`${TARGET_URL}${queryString}`)
    allAppData = response?.data
    endCPL({
      url: TARGET_URL,
      log: LOADING_APPS,
      status: response.status,
    })
  } catch (err) {
    endCPL({
      url: TARGET_URL,
      log: LOADING_APPS,
      status: 500,
      err,
    })

    throw err
  }
  return allAppData
}

apiFactory.loadAllAccount = async () => {
  const TARGET_URL = '/dashboard/api/accounts'
  const LOADING_ALL_ACCOUNTS = 'Loading all accounts'
  let allAccountData
  try {
    startCPL({ url: TARGET_URL, log: LOADING_ALL_ACCOUNTS })
    const response = await axios.get(TARGET_URL)

    allAccountData = response?.data
    endCPL({
      url: TARGET_URL,
      log: LOADING_ALL_ACCOUNTS,
      status: response.status,
    })
  } catch (err) {
    endCPL({
      url: TARGET_URL,
      log: LOADING_ALL_ACCOUNTS,
      status: 500,
      err,
    })
  }
  return allAccountData
}

apiFactory.loadAccountDetails = async ({ accountId }) => {
  const TARGET_URL = '/dashboard/api/accounts/detail'
  const LOADING_ACCOUNT_DETAILS = 'Loading account details'
  let accountApiData
  if (!accountId) {
    return accountApiData
  }
  startCPL({
    url: TARGET_URL,
    log: LOADING_ACCOUNT_DETAILS,
  })
  try {
    const response = await axios.get(`${TARGET_URL}/${accountId}`, {
      headers: { 'Content-Type': APPLICATION_JSON },
    })
    accountApiData = response.data
    endCPL({
      url: TARGET_URL,
      log: LOADING_ACCOUNT_DETAILS,
      status: response.status,
    })
  } catch (err) {
    endCPL({
      url: TARGET_URL,
      log: LOADING_ACCOUNT_DETAILS,
      status: 500,
    })
  }
  return accountApiData
}

apiFactory.loadAppsCredentialUsingClientId = async ({
  clientId,
  servermigration,
}) => {
  let TARGET_URL
  if (servermigration === ELMO.CTRL_DEVELOPER_SERVER_MIGRATION) {
    TARGET_URL = '/developer/api/applications/edit/'
  } else {
    TARGET_URL = '/dashboard/api/applications/edit/'
  }
  const LOAD_CREDENTIALS_BY_CLIENT = 'loadAppsCredentialUsingClientId'
  let appsCredentials = []
  if (!clientId) {
    return appsCredentials
  }
  try {
    startCPL({
      url: TARGET_URL,
      log: LOAD_CREDENTIALS_BY_CLIENT,
    })
    const configs = {
      headers: { 'Content-Type': APPLICATION_JSON },
      params: {
        appname: clientId,
        limit: 'apps',
      },
    }
    const response = await axios.get(`${TARGET_URL}${clientId}`, configs)
    if (!response) {
      logger.log(
        'There are no app credentials returned from the server using encrypted client id.',
      )
    } else if (
      response?.data?.app?.credentials ||
      response?.data?.data?.app?.credentials
    ) {
      appsCredentials = response.data.app ?? response.data.data.app
    }
    endCPL({
      url: TARGET_URL,
      log: LOAD_CREDENTIALS_BY_CLIENT,
      status: response.status,
    })
  } catch (err) {
    endCPL({
      url: TARGET_URL,
      log: LOAD_CREDENTIALS_BY_CLIENT,
      status: 500,
      err,
    })
  }
  return appsCredentials
}

apiFactory.loadCreditCardDetails = async ({ csrf }) => {
  const TARGET_URL = '/dashboard/api/creditCardGenerator'
  const LOAD_CARD_DETAILS = 'Loading CreditCardDetails'
  let cardApiData
  try {
    startCPL({
      url: TARGET_URL,
      log: LOAD_CARD_DETAILS,
    })
    const response = await axios({
      url: TARGET_URL,
      method: 'post',
      headers: {
        'Content-Type': APPLICATION_JSON,
        'x-csrf-token': csrf,
      },
    })

    cardApiData = response?.data
    endCPL({
      url: TARGET_URL,
      log: LOAD_CARD_DETAILS,
      status: response.status,
    })
  } catch (err) {
    endCPL({
      url: TARGET_URL,
      log: LOAD_CARD_DETAILS,
      status: 500,
      err,
    })
  }
  return cardApiData
}

apiFactory.loadAppsCredentialUsingAccountNumber = async ({ csrf }) => {
  const TARGET_URL = '/dashboard/api/resthistory/live'
  const LOAD_CREDENTIALS_BY_ACCOUNT = 'Loading AppsCredentialUsingAccountNumber'
  let appsCredentials = []
  try {
    startCPL({
      url: TARGET_URL,
      log: LOAD_CREDENTIALS_BY_ACCOUNT,
    })
    const configs = {
      headers: {
        Accept: APPLICATION_JSON,
        'Content-Type': APPLICATION_JSON,
        'x-csrf-token': csrf,
      },
      params: { limit: 'apps' },
      responseType: 'json',
      contentType: APPLICATION_JSON,
    }
    appsCredentials = await axios.get(
      `/dashboard/api/resthistory/live`,
      configs,
    )

    endCPL({
      url: TARGET_URL,
      log: LOAD_CREDENTIALS_BY_ACCOUNT,
      status: appsCredentials.status,
    })
  } catch (err) {
    endCPL({
      url: TARGET_URL,
      log: LOAD_CREDENTIALS_BY_ACCOUNT,
      status: 500,
      err,
    })
  }
  return appsCredentials
}

apiFactory.loadRecentTransactions = async ({ csrf, targetUrl, urlParam }) => {
  const LOAD_TRANSACTIONS = 'Loading RecentTransactions'
  let transactionApiData
  try {
    startCPL({ url: targetUrl, log: LOAD_TRANSACTIONS })
    const configs = {
      params: urlParam,
      headers: {
        Accept: APPLICATION_JSON,
        'Content-Type': APPLICATION_JSON,
        'x-csrf-token': csrf,
      },
      responseType: 'json',
      contentType: APPLICATION_JSON,
    }

    transactionApiData = await axios.get(targetUrl, configs)
    endCPL({
      url: targetUrl,
      log: LOAD_TRANSACTIONS,
      status: transactionApiData.status,
    })
  } catch (err) {
    endCPL({
      url: targetUrl,
      log: LOAD_TRANSACTIONS,
      status: 500,
      err,
    })
  }
  return transactionApiData
}

apiFactory.loadAPIErrorDetails = async ({
  csrf,
  targetUrl,
  apiCallsUrlParam,
  environment,
}) => {
  const LOAD_API_ERRORS = 'Loading APIErrorDetails'
  let apiErrorData = []
  try {
    startCPL({ url: targetUrl, log: LOAD_API_ERRORS })

    const configs = {
      params: apiCallsUrlParam,
      headers: {
        Accept: APPLICATION_JSON,
        'Content-Type': APPLICATION_JSON,
        'x-csrf-token': csrf,
      },
      responseType: 'json',
      contentType: APPLICATION_JSON,
    }
    const data = await axios.get(targetUrl, configs)
    apiErrorData = environment === 'sandbox' ? data?.data : data?.data?.data

    endCPL({
      url: targetUrl,
      log: LOAD_API_ERRORS,
      status: data.status,
    })
  } catch (err) {
    endCPL({
      url: targetUrl,
      log: LOAD_API_ERRORS,
      status: 500,
      err,
    })
  }
  return apiErrorData
}

apiFactory.loadWebhookErrorDetails = async ({ csrf, targetUrl, params }) => {
  const LOAD_WEBHOOK_ERRORS = 'Loading WebhookErrorDetails'
  let webhooksErrorData = []
  try {
    startCPL({ url: targetUrl, log: LOAD_WEBHOOK_ERRORS })
    const configs = {
      params,
      headers: {
        Accept: APPLICATION_JSON,
        'Content-Type': APPLICATION_JSON,
        'x-csrf-token': csrf,
      },
      responseType: 'json',
      contentType: APPLICATION_JSON,
    }

    webhooksErrorData = await axios.get(targetUrl, configs)
    logger.log('loadWebhookErrorDetails completed')

    endCPL({
      url: targetUrl,
      log: LOAD_WEBHOOK_ERRORS,
      status: webhooksErrorData.status,
    })
  } catch (err) {
    endCPL({
      url: targetUrl,
      log: LOAD_WEBHOOK_ERRORS,
      status: 500,
      err,
    })
  }
  return webhooksErrorData
}

apiFactory.loadAPIWebhookErrorDetail = async ({ csrf, targetUrl, params }) => {
  const LOAD_API_WEBHOOK_ERRORS = 'Loading APIWebhookErrorDetails'
  let apiWebhooksErrorData = []
  try {
    startCPL({ url: targetUrl, log: LOAD_API_WEBHOOK_ERRORS })
    const configs = {
      params,
      headers: {
        Accept: APPLICATION_JSON,
        'Content-Type': APPLICATION_JSON,
        'x-csrf-token': csrf,
      },
      responseType: 'json',
      contentType: APPLICATION_JSON,
    }

    apiWebhooksErrorData = await axios.get(targetUrl, configs)

    endCPL({
      url: targetUrl,
      log: LOAD_API_WEBHOOK_ERRORS,
      status: apiWebhooksErrorData.status,
    })
  } catch (err) {
    endCPL({
      url: targetUrl,
      log: LOAD_API_WEBHOOK_ERRORS,
      status: 500,
      err,
    })
  }
  return apiWebhooksErrorData
}
apiFactory.loadAPIWebhookAttemptHistory = async ({ csrf, url, params }) => {
  const LOAD_API_WEBHOOK_HISTORY = 'Loading loadAPIWebhookAttemptHistory'
  let apiWebhooksErrorData = []
  try {
    startCPL({ url, log: LOAD_API_WEBHOOK_HISTORY })
    const configs = {
      params,
      headers: {
        Accept: APPLICATION_JSON,
        'Content-Type': APPLICATION_JSON,
        'x-csrf-token': csrf,
      },
      responseType: 'json',
      contentType: APPLICATION_JSON,
    }

    apiWebhooksErrorData = await axios.get(url, configs)

    endCPL({
      url,
      log: LOAD_API_WEBHOOK_HISTORY,
      status: apiWebhooksErrorData.status,
    })
  } catch (err) {
    endCPL({
      url,
      log: LOAD_API_WEBHOOK_HISTORY,
      status: 500,
      err,
    })
  }
  return apiWebhooksErrorData
}

apiFactory.loadSandboxAppData = async ({ csrf }) => {
  const TARGET_URL = '/dashboard/api/resthistory/sandbox'
  const LOAD_SB_APPS = 'Loading sandbox apps'
  let sbAppData
  try {
    startCPL({
      url: TARGET_URL,
      log: LOAD_SB_APPS,
    })
    const configs = {
      headers: {
        Accept: APPLICATION_JSON,
        'Content-Type': APPLICATION_JSON,
        'x-csrf-token': csrf,
      },
      responseType: 'json',
      contentType: APPLICATION_JSON,
    }
    const response = await axios.get(TARGET_URL, configs)
    sbAppData = response?.data
    endCPL({
      url: TARGET_URL,
      log: LOAD_SB_APPS,
      status: response.status,
    })
  } catch (err) {
    endCPL({
      url: TARGET_URL,
      log: LOAD_SB_APPS,
      status: 500,
      err,
    })

    throw err
  }
  return sbAppData
}

apiFactory.loadAPICallsDetail = async ({ csrf, params }) => {
  const TARGET_URL = '/dashboard/api/resthistory/detail'
  const LOAD_API_DETAILS = 'Loading APICallsDetail'
  let apiWebhooksErrorData = []
  try {
    startCPL({
      url: TARGET_URL,
      log: LOAD_API_DETAILS,
    })
    const configs = {
      params,
      headers: {
        Accept: APPLICATION_JSON,
        'Content-Type': APPLICATION_JSON,
        'x-csrf-token': csrf,
      },
      responseType: 'json',
      contentType: APPLICATION_JSON,
    }

    apiWebhooksErrorData = await axios.get(TARGET_URL, configs)

    endCPL({
      url: TARGET_URL,
      log: LOAD_API_DETAILS,
      status: apiWebhooksErrorData.status,
    })
  } catch (err) {
    endCPL({
      url: TARGET_URL,
      log: LOAD_API_DETAILS,
      status: 500,
      err,
    })
  }
  return apiWebhooksErrorData
}

apiFactory.getUserPreferences = async () => {
  const TARGET_URL = '/dashboard/userPreferences'
  const GET_USER_PREFS = 'getUserPreferences'
  try {
    startCPL({
      url: TARGET_URL,
      log: GET_USER_PREFS,
    })
    const response = await axios.get(`/dashboard/userPreferences`)

    endCPL({
      url: TARGET_URL,
      log: GET_USER_PREFS,
      status: response.status,
    })
    return response?.data ? response?.data : []
  } catch (error) {
    endCPL({
      url: TARGET_URL,
      log: GET_USER_PREFS,
      status: error.response.status,
      error,
    })
    return []
  }
}

apiFactory.addUserPreference = async (csrf, preference) => {
  const TARGET_URL = '/dashboard/userPreferences'
  const ADD_USER_PREFS = 'addUserPreference'
  try {
    startCPL({
      url: TARGET_URL,
      log: ADD_USER_PREFS,
    })

    const response = await axios.post(TARGET_URL, preference, {
      headers: {
        'x-csrf-token': csrf,
      },
    })

    endCPL({
      url: TARGET_URL,
      log: ADD_USER_PREFS,
      status: response.status,
    })
  } catch (error) {
    endCPL({
      url: TARGET_URL,
      log: ADD_USER_PREFS,
      status: error.response.status,
      error,
    })
  }
}

apiFactory.apiCredGenerateSecret = async (csrf, data) => {
  const TARGET_URL = '/dashboard/api/applications/createsecret'
  const GENERATE_SECRET = 'apiCredGenerateSecret'
  let response
  try {
    startCPL({
      url: TARGET_URL,
      log: GENERATE_SECRET,
    })
    response = await axios.post(TARGET_URL, data, {
      headers: {
        Accept: APPLICATION_JSON,
        'Content-Type': APPLICATION_JSON,
        'x-csrf-token': csrf,
      },
    })
    endCPL({
      url: TARGET_URL,
      log: GENERATE_SECRET,
      status: response.status,
    })
  } catch (error) {
    endCPL({
      url: TARGET_URL,
      log: GENERATE_SECRET,
      status: error.response.status,
      error,
    })
  }
  return response
}

apiFactory.apiCredDeleteSecret = async (csrf, info) => {
  const TARGET_URL = '/dashboard/api/applications/deletesecret'
  const DELETE_SECRET = 'apiCredDeleteSecret'
  let response
  try {
    startCPL({
      url: TARGET_URL,
      log: DELETE_SECRET,
    })
    const configs = {
      headers: {
        Accept: APPLICATION_JSON,
        'Content-Type': APPLICATION_JSON,
        'x-csrf-token': csrf,
      },
      data: info,
      responseType: 'json',
      contentType: APPLICATION_JSON,
    }
    response = await axios.delete(TARGET_URL, configs)

    endCPL({
      url: TARGET_URL,
      log: DELETE_SECRET,
      status: response.status,
    })
  } catch (err) {
    endCPL({
      url: TARGET_URL,
      log: DELETE_SECRET,
      status: 500,
      err,
    })
    return err.response
  }
  return response
}

apiFactory.apiCredToggleSecret = async (csrf, data) => {
  const TARGET_URL = '/dashboard/api/applications/updatesecret'
  const TOGGLE_SECRET_STATUS = 'apiCredToggleSecretKeyStatus'
  let response
  try {
    startCPL({
      url: TARGET_URL,
      log: TOGGLE_SECRET_STATUS,
    })
    response = await axios.patch(TARGET_URL, data, {
      headers: {
        Accept: APPLICATION_JSON,
        'Content-Type': APPLICATION_JSON,
        'x-csrf-token': csrf,
      },
    })
    endCPL({
      url: TARGET_URL,
      log: TOGGLE_SECRET_STATUS,
      status: response.status,
    })
  } catch (error) {
    endCPL({
      url: TARGET_URL,
      log: TOGGLE_SECRET_STATUS,
      status: error.response.status,
      error,
    })
    return error.response
  }
  return response
}

apiFactory.sendUnauthEmail = async (csrf) => {
  const TARGET_URL = '/dashboard/api/sendAdminEmail'
  const SEND_EMAIL = 'sendEmailForUnauthUser'
  let response
  try {
    startCPL({
      url: TARGET_URL,
      log: SEND_EMAIL,
    })
    response = await axios.post(
      TARGET_URL,
      {},
      {
        headers: {
          Accept: APPLICATION_JSON,
          'Content-Type': APPLICATION_JSON,
          'x-csrf-token': csrf,
        },
      },
    )
    endCPL({
      url: TARGET_URL,
      log: SEND_EMAIL,
      status: response.status,
    })
  } catch (error) {
    endCPL({
      url: TARGET_URL,
      log: SEND_EMAIL,
      status: error.response.status,
      error,
    })
    throw error
  }
  return response
}

apiFactory.viewAllSellerAPI = async (csrf, entityType) => {
  return await axios.get(`/dashboard/api/developerInvites/activityapproval`, {
    headers: {
      Accept: APPLICATION_JSON,
      'Content-Type': APPLICATION_JSON,
      'x-csrf-token': csrf,
    },
    params: {
      entityType,
      // entityType:'PUBLIC_CREDENTIAL',
      page_size: 50,
    },
    responseType: 'json',
    contentType: APPLICATION_JSON,
  })
}

apiFactory.getSellerInvitation = async ({ csrf }) => {
  const TARGET_URL = '/dashboard/api/developerInvites/activityapproval'
  const LOAD_INVITATION = 'Loading seller invitation'
  let sellerInvitationData
  let response
  startCPL({
    url: TARGET_URL,
    log: LOAD_INVITATION,
  })
  try {
    response = await apiFactory.viewAllSellerAPI(csrf, 'PUBLIC_CREDENTIAL')
    if (response.data === '') {
      response = await apiFactory.viewAllSellerAPI(csrf, 'PARTY_ID')
    }

    sellerInvitationData = response.data
    endCPL({
      url: TARGET_URL,
      log: LOAD_INVITATION,
      status: response.status,
    })
  } catch (err) {
    endCPL({
      url: TARGET_URL,
      log: LOAD_INVITATION,
      status: 500,
    })
  }
  return sellerInvitationData
}

apiFactory.getLivePayloadData = async (csrf, payload) => {
  const TARGET_URL = '/dashboard/api/upgradehub/transform-integrations'
  const GET_PAYLOAD = 'getLivePayloadData'
  try {
    startCPL({
      url: TARGET_URL,
      log: GET_PAYLOAD,
    })
    const response = await axios.post(TARGET_URL, payload, {
      headers: {
        Accept: APPLICATION_JSON,
        'Content-Type': APPLICATION_JSON,
        'x-csrf-token': csrf,
      },
      responseType: 'json',
      contentType: APPLICATION_JSON,
    })
    //throw error if no response
    if (!response || response.status !== 200) {
      throw Error('Failed to get live payload data')
    }

    endCPL({
      url: TARGET_URL,
      log: GET_PAYLOAD,
      status: response.status,
    })
    const data = response?.data
    return data ?? []
  } catch (error) {
    endCPL({
      url: TARGET_URL,
      log: 'getLivePayloadData Error',
      status: error.response.status,
      error,
    })
    throw error
  }
}

export default apiFactory
